import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArtistVideoGrid from './components/ArtistVideoGrid';
import MusicPlayer from '../components/MusicPlayer';
import { fetchMediaPosts, fetchMediaTracks, formatCount, CERTIFICATION_VIEWS } from '@/lib/media';
import type { MediaPost, MediaTrack } from '@/lib/media';
import { demoVideos, demoTracks } from '@/mocks/media';

const DEFAULT_BANNER =
  'https://readdy.ai/api/search-image?query=Warm%20abstract%20concert%20stage%20with%20golden%20bokeh%20lights%20and%20soft%20amber%20haze%2C%20silhouettes%20of%20musicians%20and%20instruments%2C%20cinematic%20depth%20of%20field%2C%20rich%20terracotta%20and%20gold%20tones%2C%20elegant%20minimal%20artistic%20banner%20background&width=1600&height=600&seq=artist-banner-default&orientation=landscape';

type Tab = 'videos' | 'musique';

function ArtistProfile() {
  const { slug } = useParams<{ slug: string }>();
  const artistName = slug || '';

  const [tab, setTab] = useState<Tab>('videos');
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [tracks, setTracks] = useState<MediaTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [following, setFollowing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, t] = await Promise.all([fetchMediaPosts(), fetchMediaTracks()]);
      setPosts(p);
      setTracks(t);
    } catch {
      setError('Impossible de charger le profil. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allVideos = useMemo<MediaPost[]>(() => [...posts, ...demoVideos], [posts]);
  const allTracks = useMemo<MediaTrack[]>(() => [...tracks, ...demoTracks], [tracks]);

  const artistVideos = useMemo(
    () => allVideos.filter((v) => (v.artist_name || '') === artistName),
    [allVideos, artistName],
  );
  const artistTracks = useMemo(
    () => allTracks.filter((t) => (t.artist_name || '') === artistName),
    [allTracks, artistName],
  );

  const totalViews = useMemo(
    () =>
      artistVideos.reduce((sum, v) => sum + (v.views || 0), 0) +
      artistTracks.reduce((sum, t) => sum + (t.plays || 0), 0),
    [artistVideos, artistTracks],
  );
  const totalLikes = useMemo(
    () =>
      artistVideos.reduce((sum, v) => sum + (v.likes || 0), 0) +
      artistTracks.reduce((sum, t) => sum + (t.likes || 0), 0),
    [artistVideos, artistTracks],
  );

  const location = artistVideos[0]?.location || artistTracks[0]?.location || '';
  const categories = useMemo(() => {
    const set = new Set<string>();
    artistVideos.forEach((v) => set.add(v.category));
    artistTracks.forEach((t) => set.add(t.category));
    return Array.from(set);
  }, [artistVideos, artistTracks]);

  const banner = useMemo(() => {
    const cover = artistTracks.find((t) => t.cover_url)?.cover_url;
    const thumb = artistVideos.find((v) => v.thumbnail_url)?.thumbnail_url;
    return cover || thumb || DEFAULT_BANNER;
  }, [artistTracks, artistVideos]);

  const initial = (artistName.trim().charAt(0) || 'A').toUpperCase();

  const isCertified = useMemo(
    () => artistVideos.some((v) => (v.views || 0) >= CERTIFICATION_VIEWS),
    [artistVideos],
  );

  const handleShare = async () => {
    const data = { title: artistName, url: window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {
        /* annulé */
      }
    } else {
      navigator.clipboard?.writeText(window.location.href).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Bannière */}
        <section className="relative h-[260px] md:h-[340px] overflow-hidden">
          <img
            src={banner}
            alt={artistName}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/40 to-background-950/20" />
        </section>

        {/* En-tête profil */}
        <section className="w-full px-4 md:px-8 lg:px-12">
          <div className="-mt-16 md:-mt-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-background-50 bg-primary-500 flex items-center justify-center shrink-0">
                  <span className="font-heading font-bold text-4xl md:text-5xl text-foreground-950">
                    {initial}
                  </span>
                </div>
                <div className="pb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50">
                      {artistName}
                    </h1>
                    {isCertified ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-500/15 text-accent-400 text-xs font-semibold">
                        <i className="ri-verified-badge-fill" />
                        Certifié
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/15 text-primary-400 text-xs font-semibold">
                        <i className="ri-user-star-line" />
                        Artiste
                      </span>
                    )}
                  </div>
                  {location && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-400">
                      <i className="ri-map-pin-line text-accent-400" />
                      {location}
                    </p>
                  )}
                  {categories.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {categories.slice(0, 4).map((c) => (
                        <span
                          key={c}
                          className="inline-block px-2.5 py-1 rounded-full bg-background-100 border border-background-300/60 text-xs text-foreground-400"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-50 hover:bg-background-100 transition-colors"
                  aria-label="Partager"
                >
                  <i className="ri-share-forward-line text-xl" />
                </button>
                <button
                  onClick={() => setFollowing((f) => !f)}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                    following
                      ? 'bg-background-100 border border-background-300/60 text-foreground-50 hover:bg-background-200'
                      : 'bg-primary-500 text-foreground-950 hover:bg-primary-400'
                  }`}
                >
                  <i className={following ? 'ri-check-line text-lg' : 'ri-add-line text-lg'} />
                  {following ? 'Suivi' : 'Suivre'}
                </button>
              </div>
            </div>
          </div>

          {/* Statistiques */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <div className="flex items-center gap-2 text-foreground-400 text-sm mb-1">
                <i className="ri-video-line text-accent-400" />
                Vidéos
              </div>
              <span className="font-heading font-bold text-2xl text-foreground-50">
                {artistVideos.length}
              </span>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <div className="flex items-center gap-2 text-foreground-400 text-sm mb-1">
                <i className="ri-music-2-line text-accent-400" />
                Morceaux
              </div>
              <span className="font-heading font-bold text-2xl text-foreground-50">
                {artistTracks.length}
              </span>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <div className="flex items-center gap-2 text-foreground-400 text-sm mb-1">
                <i className="ri-eye-line text-accent-400" />
                Vues & écoutes
              </div>
              <span className="font-heading font-bold text-2xl text-foreground-50">
                {formatCount(totalViews)}
              </span>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5">
              <div className="flex items-center gap-2 text-foreground-400 text-sm mb-1">
                <i className="ri-heart-3-line text-accent-400" />
                J&apos;aime
              </div>
              <span className="font-heading font-bold text-2xl text-foreground-50">
                {formatCount(totalLikes)}
              </span>
            </div>
          </div>
        </section>

        {/* Contenu */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          {/* Fil d'ariane */}
          <Link
            to="/media"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-400 hover:text-foreground-50 transition-colors mb-6"
          >
            <i className="ri-arrow-left-line" />
            Retour à Média
          </Link>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-background-100 rounded-full w-full sm:w-auto sm:max-w-xs mb-8">
            <button
              onClick={() => setTab('videos')}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                tab === 'videos'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-400 hover:text-foreground-50'
              }`}
            >
              <i className="ri-video-line mr-1.5" />
              Vidéos ({artistVideos.length})
            </button>
            <button
              onClick={() => setTab('musique')}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                tab === 'musique'
                  ? 'bg-primary-500 text-foreground-950'
                  : 'text-foreground-400 hover:text-foreground-50'
              }`}
            >
              <i className="ri-music-2-line mr-1.5" />
              Musique ({artistTracks.length})
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center gap-3 text-foreground-400">
                <i className="ri-loader-4-line animate-spin text-2xl" />
                <span>Chargement du profil…</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-error-warning-line text-3xl text-accent-400" />
              </div>
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={loadData}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : tab === 'videos' ? (
            <ArtistVideoGrid videos={artistVideos} />
          ) : (
            <MusicPlayer tracks={artistTracks} />
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ArtistProfile;